import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  InspectionReference,
  ParkingAgentService,
  ReferenceOption,
  agentError,
} from 'services/ParkingAgentService';
import {
  REFERENCE_KINDS,
  suggestReferenceColumns,
  suggestReferenceKind,
} from './referenceMapping';

interface ReferenceSelectorProps {
  siteId: number;
  disabled: boolean;
  onChange: (
    reference: InspectionReference | undefined,
    ready: boolean,
  ) => void;
}

const emptyColumns = {
  name_column: '',
  quantity_column: '',
  floor_column: '',
  unit_column: '',
  ip_column: '',
  end_ip_column: '',
};

const ReferenceSelector: React.FC<ReferenceSelectorProps> = ({
  siteId,
  disabled,
  onChange,
}) => {
  const [source, setSource] = useState<'network' | 'material'>('network');
  const [quantityMode, setQuantityMode] = useState<
    '' | 'column' | 'rows' | 'ip_range'
  >('');
  const [page, setPage] = useState(1);
  const [enabled, setEnabled] = useState(false);
  const [options, setOptions] = useState<ReferenceOption[]>([]);
  const [documentId, setDocumentId] = useState('');
  const [columns, setColumns] = useState(emptyColumns);
  const [types, setTypes] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const selected = options.find((o) => o.document.id === documentId);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setDocumentId('');
    setOptions([]);
    ParkingAgentService.references(siteId, controller.signal, source)
      .then((data) => {
        if (!controller.signal.aborted) setOptions(data);
      })
      .catch((e) => {
        if (!controller.signal.aborted) setError(agentError(e));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [enabled, siteId, revision, source]);

  const names = useMemo(
    () => [
      ...new Set(
        selected?.document.rows
          .map((row) => (row.cells[columns.name_column] || '').trim())
          .filter(Boolean) || [],
      ),
    ],
    [selected, columns.name_column],
  );
  useEffect(() => setPage(1), [names]);
  const currentPage = Math.min(
    page,
    Math.max(1, Math.ceil(Math.min(names.length, 1000) / 20)),
  );
  const mappedTypes = useMemo(
    () =>
      Object.fromEntries(
        names.map((name) => [name, types[name] ?? suggestReferenceKind(name)]),
      ),
    [names, types],
  );
  const activeColumns = useMemo(
    () => ({
      name_column: columns.name_column,
      floor_column: columns.floor_column,
      unit_column: columns.unit_column,
      quantity_column: quantityMode === 'column' ? columns.quantity_column : '',
      ...(quantityMode === 'ip_range'
        ? { ip_column: columns.ip_column, end_ip_column: columns.end_ip_column }
        : {}),
    }),
    [columns, quantityMode],
  );
  const chosenColumns = Object.values(activeColumns).filter(Boolean);
  const distinctColumns = new Set(chosenColumns).size === chosenColumns.length;
  const valid =
    !!selected &&
    !!columns.name_column &&
    (quantityMode === 'column'
      ? !!columns.quantity_column
      : quantityMode === 'rows' ||
        (quantityMode === 'ip_range' && !!columns.ip_column)) &&
    distinctColumns &&
    names.length > 0 &&
    names.length <= 1000 &&
    selected.document.rows.length <= 10000 &&
    names.every((name) => !!mappedTypes[name]) &&
    names.some((name) => mappedTypes[name] !== 'exclude');

  useEffect(() => {
    if (!enabled) {
      onChange(undefined, true);
      return;
    }
    onChange(
      valid && selected
        ? {
            document_id: selected.document.id,
            sha256: selected.sha256,
            source,
            quantity_mode: quantityMode || 'column',
            ...activeColumns,
            types: mappedTypes,
          }
        : undefined,
      valid && !loading && !error,
    );
  }, [
    enabled,
    valid,
    selected,
    activeColumns,
    source,
    quantityMode,
    mappedTypes,
    loading,
    error,
    onChange,
  ]);

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
      <Stack spacing={2}>
        <FormControlLabel
          control={
            <Checkbox
              checked={enabled}
              disabled={disabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
          }
          label="기준 문서와 수량 비교"
        />
        <Typography variant="body2" color="text.secondary">
          IP/네트워크에 등록된 문서의 컬럼을 선택해 맵·NVR·등록 자재의 수량 비교
          기준을 만듭니다.
        </Typography>
        {enabled && (
          <>
            <TextField
              select
              label="기준 자료 위치"
              value={source}
              disabled={disabled || loading}
              onChange={(e) => {
                setSource(e.target.value as 'network' | 'material');
                setDocumentId('');
                setOptions([]);
                setTypes({});
              }}
            >
              <MenuItem value="network">IP/네트워크</MenuItem>
              <MenuItem value="material">자재 문서</MenuItem>
            </TextField>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                href={
                  source === 'network'
                    ? `/project/${siteId}?section=network`
                    : `/project/${siteId}/materials`
                }
                sx={{ minHeight: 44 }}
              >
                {source === 'network'
                  ? 'IP/네트워크 문서 열기'
                  : '자재 문서 등록·확인'}
              </Button>
              <Button
                disabled={disabled || loading}
                onClick={() => setRevision((v) => v + 1)}
                sx={{ minHeight: 44 }}
              >
                기준 문서 새로고침
              </Button>
            </Stack>
            {loading && (
              <Typography role="status">기준 문서를 불러오는 중…</Typography>
            )}
            {error && <Alert severity="error">{error}</Alert>}
            {!loading && !error && options.length === 0 && (
              <Alert severity="info">
                {source === 'network' ? 'IP/네트워크' : '자재 문서 (엑셀)'}에
                기준 파일을 등록해주세요.
              </Alert>
            )}
            {options.length > 0 && (
              <TextField
                select
                label="점검 기준 문서"
                value={documentId}
                disabled={disabled || loading}
                onChange={(e) => {
                  const option = options.find(
                    (o) => o.document.id === e.target.value,
                  );
                  setDocumentId(e.target.value);
                  setColumns(
                    option
                      ? suggestReferenceColumns(option.document)
                      : emptyColumns,
                  );
                  setQuantityMode(
                    option &&
                      suggestReferenceColumns(option.document).quantity_column
                      ? 'column'
                      : '',
                  );
                  setTypes({});
                }}
              >
                <MenuItem value="">문서를 선택해주세요</MenuItem>
                {options.map((option) => (
                  <MenuItem key={option.document.id} value={option.document.id}>
                    {option.document.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {selected && (
              <>
                <TextField
                  select
                  label="기준 수량 계산"
                  value={quantityMode}
                  disabled={disabled}
                  onChange={(e) =>
                    setQuantityMode(e.target.value as typeof quantityMode)
                  }
                >
                  <MenuItem value="">계산 방식을 선택해주세요</MenuItem>
                  <MenuItem value="column">선택한 수량 열 합산</MenuItem>
                  <MenuItem value="rows">장비 1행당 1개</MenuItem>
                  <MenuItem value="ip_range">시작·끝 IP 범위 개수</MenuItem>
                </TextField>
                {quantityMode === 'rows' && (
                  <Alert severity="info">
                    한 행이 장비 1개인 목록에 사용하세요. 한 행에 여러 장비나 IP
                    범위가 적혀 있으면 다른 계산 방식을 선택해주세요.
                  </Alert>
                )}
                {quantityMode === 'ip_range' && (
                  <Alert severity="warning">
                    시작·끝 주소를 모두 포함한 IP 개수를 기준 수량으로
                    사용합니다. 예약·미사용 주소도 포함되므로 실제 설치 대상
                    범위인지 확인해주세요. 끝 IP가 비어 있으면 시작 IP 1개로
                    계산합니다.
                  </Alert>
                )}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                  {(
                    [
                      ['name_column', '품목 열', false],
                      ['quantity_column', '기준 수량 열', false],
                      ['ip_column', '시작 IP 열', false],
                      ['end_ip_column', '끝 IP 열', true],
                      ['floor_column', '층 열', true],
                      ['unit_column', '단위 열', true],
                    ] as const
                  )
                    .filter(([field]) =>
                      field === 'quantity_column'
                        ? quantityMode === 'column'
                        : field === 'ip_column' || field === 'end_ip_column'
                          ? quantityMode === 'ip_range'
                          : true,
                    )
                    .map(([field, label, optional]) => (
                      <TextField
                        fullWidth
                        select
                        key={field}
                        label={label}
                        value={columns[field]}
                        disabled={disabled}
                        onChange={(e) =>
                          setColumns((previous) => ({
                            ...previous,
                            [field]: e.target.value,
                          }))
                        }
                      >
                        <MenuItem value="">
                          {optional
                            ? field === 'end_ip_column'
                              ? '시작 IP 1개만 사용'
                              : field === 'floor_column'
                                ? '현장 전체 합계'
                                : '개수 기준 (개)'
                            : '열을 선택해주세요'}
                        </MenuItem>
                        {selected.document.columns.map((column) => (
                          <MenuItem key={column.id} value={column.id}>
                            {column.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    ))}
                </Stack>
                {!distinctColumns && (
                  <Alert severity="warning">
                    각 항목에 서로 다른 열을 선택해주세요.
                  </Alert>
                )}
                <Alert severity="info">
                  층 열을 선택하면 층별로, 선택하지 않으면 현장 전체 수량을
                  비교합니다. 같은 종류·층의 품목은 합산합니다. 합계 행은 ‘비교
                  제외’로 지정해주세요. NVR은 중복 IP를 제외하고 맵에서 층·CCTV
                  종류를 확인합니다.
                </Alert>
                <Typography variant="subtitle2">
                  품목별 비교 종류 · {names.length}개
                </Typography>
                <Stack
                  spacing={1}
                  sx={{ maxHeight: 360, overflowY: 'auto', p: 0.5 }}
                >
                  {names
                    .slice((currentPage - 1) * 20, currentPage * 20)
                    .map((name) => (
                      <Stack
                        key={name}
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ sm: 'center' }}
                      >
                        <Typography
                          sx={{
                            flex: 1,
                            overflowWrap: 'anywhere',
                            minWidth: 0,
                          }}
                        >
                          {name}
                        </Typography>
                        <TextField
                          select
                          size="small"
                          label={`${name} 비교 종류`}
                          value={mappedTypes[name]}
                          disabled={disabled}
                          onChange={(e) =>
                            setTypes((previous) => ({
                              ...previous,
                              [name]: e.target.value,
                            }))
                          }
                          sx={{ width: { xs: '100%', sm: 240 } }}
                        >
                          <MenuItem value="">직접 지정해주세요</MenuItem>
                          {REFERENCE_KINDS.map(([kind, label]) => (
                            <MenuItem key={kind} value={kind}>
                              {label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Stack>
                    ))}
                </Stack>
                {names.length > 20 && (
                  <Pagination
                    count={Math.ceil(Math.min(names.length, 1000) / 20)}
                    page={currentPage}
                    onChange={(_, value) => setPage(value)}
                    siblingCount={0}
                    size="small"
                  />
                )}
                {!valid && (
                  <Alert severity="warning">
                    품목 열·수량 계산 방식과 모든 품목의 비교 종류를
                    확인해주세요. 기준 문서는 10,000행·1,000품목까지 지원합니다.
                  </Alert>
                )}
                <Typography variant="caption" color="text.secondary">
                  현재 선택: {selected.document.name} ·{' '}
                  {selected.document.rows.length}행. 점검 시 기준 문서와 결과가
                  함께 기록됩니다.
                </Typography>
              </>
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
};

export default ReferenceSelector;
